import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import Spinner from "../Loading/Spinner";
import InlineError from "./InlineError";

// Combobox — presentation primitive. Single-select composite input
// with a text trigger and an inline popup list. Selector-free,
// dispatch-free; consumes its data via props only.
//
// Field-compatible: accepts the same id / aria-describedby /
// aria-invalid that Field injects via cloneElement, and forwards them
// to the inner <input>. See migration/phase10-composite-input-plan.md
// §4 for the architectural decision.
//
// Owns: popup open/close, keyboard navigation, outside-click,
// active-option indication, ARIA wiring.
// Does NOT own: option list contents, fetch lifecycle, navigation,
// or any feature knowledge.

const STATE_HINT_ID_SUFFIX = "-state";

function ComboboxBody({
  bodyId,
  hintId,
  loading,
  error,
  options,
  activeIndex,
  optionId,
  onOptionSelect,
  onOptionHover,
  emptyHint,
  emptyResultsLabel,
  selectedId,
}) {
  if (loading) {
    return (
      <li id={hintId} className="ui-combobox__state-row" role="presentation">
        <Spinner size="sm" label="Завантаження" />
        <span className="ui-combobox__state-text">Шукаємо…</span>
      </li>
    );
  }

  if (error) {
    return (
      <li id={hintId} className="ui-combobox__state-row" role="presentation">
        <InlineError>{error}</InlineError>
      </li>
    );
  }

  if (options.length === 0) {
    return (
      <li id={hintId} className="ui-combobox__state-row" role="presentation">
        <span className="ui-combobox__state-text">
          {emptyResultsLabel ?? emptyHint ?? "Нічого не знайдено."}
        </span>
      </li>
    );
  }

  return options.map((option, index) => {
    const isActive = index === activeIndex;
    const isCommitted =
      selectedId != null && selectedId !== "" && option.id === selectedId;
    const classes = [
      "ui-combobox__option",
      isActive ? "ui-combobox__option--active" : "",
      isCommitted ? "ui-combobox__option--selected" : "",
    ]
      .filter(Boolean)
      .join(" ");
    // Keyboard highlight is aria-activedescendant + --active.
    // When a committed selectedId is provided, aria-selected tracks
    // that commitment — not the transient highlight.
    const ariaSelected = selectedId != null && selectedId !== ""
      ? isCommitted
      : isActive;

    return (
      <li
        key={option.id}
        id={optionId(index)}
        role="option"
        aria-selected={ariaSelected ? "true" : "false"}
        className={classes}
        onMouseEnter={() => onOptionHover(index)}
        onMouseDown={(event) => {
          // Use mousedown (not click) so selection fires before the
          // input's blur cycle would otherwise close the popup.
          event.preventDefault();
          onOptionSelect(option);
        }}
      >
        <span className="ui-combobox__option-label">{option.label}</span>
        {option.sublabel ? (
          <span className="ui-combobox__option-sublabel">{option.sublabel}</span>
        ) : null}
      </li>
    );
  });
}

function Combobox({
  id,
  name,
  value,
  onChange,
  placeholder,
  disabled = false,
  required = false,
  autoComplete = "off",
  className = "",
  "aria-describedby": describedBy,
  "aria-invalid": invalid,
  "aria-label": ariaLabel,

  options = [],
  loading = false,
  error,
  emptyHint,
  emptyResultsLabel,
  onSelect,
  onOpen,
  onClose,
  selectedId,
}) {
  const rootRef = useRef(null);
  const inputRef = useRef(null);
  const popupRef = useRef(null);

  const [focused, setFocused] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);

  const hasQuery = typeof value === "string" && value.trim().length > 0;
  const showStateRow =
    focused && (loading || error || (hasQuery && options.length === 0));
  const showOptions = focused && options.length > 0;
  const open =
    focused
    && (hasQuery
      || loading
      || error
      || options.length > 0
      || Boolean(emptyHint));

  const listboxId = id ? `${id}-listbox` : undefined;
  const stateHintId = id ? `${id}${STATE_HINT_ID_SUFFIX}` : undefined;
  const optionId = useCallback(
    (index) => (id ? `${id}-option-${index}` : undefined),
    [id],
  );

  // Outside-click: close when mousedown lands outside the root.
  useEffect(() => {
    if (!open) return undefined;

    const handleDocumentMouseDown = (event) => {
      const root = rootRef.current;
      if (!root) return;
      if (root.contains(event.target)) return;
      setFocused(false);
    };

    document.addEventListener("mousedown", handleDocumentMouseDown);
    return () => {
      document.removeEventListener("mousedown", handleDocumentMouseDown);
    };
  }, [open]);

  // Clamp activeIndex into range when options change.
  useEffect(() => {
    if (options.length === 0) {
      if (activeIndex !== -1) setActiveIndex(-1);
      return;
    }
    if (activeIndex >= options.length) {
      setActiveIndex(options.length - 1);
    } else if (activeIndex < 0 && open) {
      setActiveIndex(0);
    }
  }, [options, activeIndex, open]);

  // Notify consumer on open transitions.
  const wasOpenRef = useRef(false);
  useEffect(() => {
    if (open && !wasOpenRef.current) {
      wasOpenRef.current = true;
      if (onOpen) onOpen();
    } else if (!open && wasOpenRef.current) {
      wasOpenRef.current = false;
      if (onClose) onClose();
    }
  }, [open, onOpen, onClose]);

  // Scroll the active option into view when it changes. When options
  // are rendered, popup.children[activeIndex] is the corresponding <li>
  // (state rows are mutually exclusive with option rows — see
  // ComboboxBody).
  useEffect(() => {
    if (!open || activeIndex < 0) return;
    const popup = popupRef.current;
    if (!popup) return;
    const node = popup.children[activeIndex];
    if (node && typeof node.scrollIntoView === "function") {
      node.scrollIntoView({ block: "nearest" });
    }
  }, [activeIndex, open]);

  const selectOption = useCallback(
    (option) => {
      if (!option) return;
      setFocused(false);
      setActiveIndex(-1);
      if (onSelect) onSelect(option);
    },
    [onSelect],
  );

  const handleKeyDown = (event) => {
    if (disabled) return;

    if (event.key === "ArrowDown") {
      event.preventDefault();
      if (options.length === 0) return;
      setFocused(true);
      setActiveIndex((prev) => {
        if (prev < 0) return 0;
        return (prev + 1) % options.length;
      });
      return;
    }

    if (event.key === "ArrowUp") {
      event.preventDefault();
      if (options.length === 0) return;
      setFocused(true);
      setActiveIndex((prev) => {
        if (prev < 0) return options.length - 1;
        return (prev - 1 + options.length) % options.length;
      });
      return;
    }

    if (event.key === "Home") {
      if (!open || options.length === 0) return;
      event.preventDefault();
      setActiveIndex(0);
      return;
    }

    if (event.key === "End") {
      if (!open || options.length === 0) return;
      event.preventDefault();
      setActiveIndex(options.length - 1);
      return;
    }

    if (event.key === "Enter") {
      if (!open || activeIndex < 0 || activeIndex >= options.length) {
        return;
      }
      event.preventDefault();
      selectOption(options[activeIndex]);
      return;
    }

    if (event.key === "Escape") {
      if (!open) return;
      event.preventDefault();
      setFocused(false);
      setActiveIndex(-1);
      return;
    }

    if (event.key === "Tab") {
      // Let focus move naturally; just close.
      setFocused(false);
      setActiveIndex(-1);
    }
  };

  const handleFocus = () => {
    setFocused(true);
  };

  const handleBlur = (event) => {
    // If the new focus target is inside the combobox root (e.g. a
    // future "clear" button), keep open. Otherwise close.
    const root = rootRef.current;
    const next = event.relatedTarget;
    if (root && next && root.contains(next)) return;
    // Defer close one tick so an option's mousedown selection can
    // still run before blur tears down the popup. Selection itself
    // already calls setFocused(false) inside selectOption.
    setFocused(false);
  };

  const handleChange = (event) => {
    if (onChange) onChange(event);
    // Re-open on type if focused
    if (!focused) setFocused(true);
    setActiveIndex((prev) => (prev < 0 ? 0 : prev));
  };

  const activeDescendant = useMemo(() => {
    if (!open) return undefined;
    if (showStateRow) return stateHintId;
    if (activeIndex < 0 || activeIndex >= options.length) return undefined;
    return optionId(activeIndex);
  }, [open, showStateRow, activeIndex, options.length, optionId, stateHintId]);

  const wrapperClasses = [
    "ui-combobox",
    open ? "ui-combobox--open" : "",
    disabled ? "ui-combobox--disabled" : "",
    invalid === "true" || invalid === true ? "ui-combobox--invalid" : "",
    className,
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <div className={wrapperClasses} ref={rootRef}>
      <input
        ref={inputRef}
        id={id}
        name={name}
        type="text"
        role="combobox"
        className="ui-combobox__input"
        value={value ?? ""}
        onChange={handleChange}
        onFocus={handleFocus}
        onBlur={handleBlur}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        disabled={disabled}
        required={required}
        autoComplete={autoComplete}
        aria-label={ariaLabel}
        aria-autocomplete="list"
        aria-expanded={open ? "true" : "false"}
        aria-controls={open ? listboxId : undefined}
        aria-activedescendant={activeDescendant}
        aria-haspopup="listbox"
        aria-describedby={describedBy}
        aria-invalid={invalid}
      />

      {open ? (
        <ul
          ref={popupRef}
          id={listboxId}
          role="listbox"
          className="ui-combobox__popup"
        >
          {showOptions || showStateRow ? (
            <ComboboxBody
              bodyId={listboxId}
              hintId={stateHintId}
              loading={loading}
              error={error}
              options={options}
              activeIndex={activeIndex}
              optionId={optionId}
              onOptionSelect={selectOption}
              onOptionHover={setActiveIndex}
              emptyHint={emptyHint}
              emptyResultsLabel={emptyResultsLabel}
              selectedId={selectedId}
            />
          ) : emptyHint ? (
            <li
              id={stateHintId}
              className="ui-combobox__state-row"
              role="presentation"
            >
              <span className="ui-combobox__state-text">{emptyHint}</span>
            </li>
          ) : null}
        </ul>
      ) : null}
    </div>
  );
}

export default Combobox;
